import {
  ApiAttributionTag,
  ApiBucketResponse,
  ApiConditionCustom,
  ApiProject,
} from "@incendium/api";
import { Box, Stack, Typography } from "@mui/material";
import SourceRules from "features/channels/components/SourceRules";
import { useConditionGroupContext } from "Components/ConditionalGroup/ConditionGroupProvider";
import {
  SidebarContentWrapper,
  SidebarStyledDivider,
  SiderBarHeader,
} from "Components/UI/Sidebar";
import ConditionGroupsFormWrapper from "Components/ConditionalGroup/ConditionGroupsFormWrapper";
import { useTrafficBuckets } from "Hooks/useTrafficBuckets";
import produce from "immer";
import { useNotification } from "Hooks";
import { useCallback, useEffect } from "react";
import { TextValidator } from "react-material-ui-form-validator";
import { useHistory } from "react-router-dom";
import AttributionTagSelector from "Components/AttributionTag/AttributionTagSelector";
import { saveTrafficBucket } from "features/traffic/services/trafficBucketService";

interface ITrafficBucketFormProps {
  bucket: ApiBucketResponse;
  setBucket: React.Dispatch<
    React.SetStateAction<ApiBucketResponse | undefined>
  >;
  project: ApiProject;
  isNew?: boolean;
}

function TrafficBucketForm({
  bucket,
  setBucket,
  project,
  isNew,
}: ITrafficBucketFormProps) {
  const history = useHistory();
  const { conditionGroups, setConditionGroups } = useConditionGroupContext();
  const { trafficBuckets, setTrafficBuckets } = useTrafficBuckets();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  useEffect(() => {
    if (!bucket || conditionGroups.length) {
      return;
    }

    const groups = bucket.groups || [];

    // parse bucket groups as condition groups
    const parsedCGs = groups.map((g) => ({
      ...g,
      conditions: (g.rules || []).map((r) => ({
        ...r,
        custom: ApiConditionCustom.UNSPECIFIED,
        conversionId: null,
      })),
    }));

    setConditionGroups(
      parsedCGs && parsedCGs.length > 0
        ? [...parsedCGs]
        : [
          {
            conditions: [{}],
          },
        ]
    );
  }, [bucket, setConditionGroups, conditionGroups]);

  const onAttributionTagChange = useCallback(
    (tags: ApiAttributionTag[]) => {
      setBucket(
        produce(bucket, (draft) => {
          draft.tags = tags;
        })
      );
    },
    [bucket, setBucket]
  );

  const onSubmit = async () => {
    // Check if condition groups are in default state (single group with empty condition)
    const isDefaultState = conditionGroups.length === 1 &&
      conditionGroups[0]?.conditions?.length === 1 &&
      Object.keys(conditionGroups[0].conditions[0] || {}).length === 0;

    const parsedCGs = conditionGroups.map((cg) => ({
      ...cg,
      rules: cg.conditions || [],
    }));


    try {
      const res = await saveTrafficBucket(project.id as number, {
        id: bucket.id,
        name: bucket.name,
        groups: isDefaultState ? [] : parsedCGs,
        tags: (bucket.tags || []),
      });

      setTrafficBuckets(
        produce(trafficBuckets, (draft) => {
          if (isNew) {
            draft.push(res);
            return;
          }
          const idx = draft.findIndex((d) => d.id === res.id);
          if (idx >= 0) {
            draft[idx] = res;
          }
        })
      );
      showSuccessNotification(`${res.name} Saved`);
      if (isNew && history.location.pathname.includes("new")) {
        history.push(`../buckets/${res.id}`);
      }
    } catch (error) {
      showErrorNotification("Error saving traffic bucket");
    }
  };

  const onCancel = () => {
    history.push(`../buckets`);
  };

  return (
    <>
      <ConditionGroupsFormWrapper
        onSubmit={onSubmit}
        onCancel={onCancel}
        rules={<SourceRules />}
      >
        <SiderBarHeader>
          <Box
            display="flex"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography justifyContent="space-between" variant="h2">
              {bucket.name} Settings
            </Typography>
          </Box>
          <Typography variant="body1">
            Assign a name to this traffic bucket. You can also select tags for
            this bucket for grouping in analytics
          </Typography>
        </SiderBarHeader>
        <SidebarStyledDivider />
        <SidebarContentWrapper px={3}>
          <Stack spacing={2} mb={3.5} mt={2}>
            <TextValidator
              label="Name of Traffic Bucket"
              variant="outlined"
              name="name"
              fullWidth
              validators={["required"]}
              errorMessages={["name is required"]}
              value={bucket.name || ""}
              onChange={(e: React.FormEvent<HTMLInputElement>) => {
                setBucket(
                  produce(bucket, (draft) => {
                    draft.name = e.currentTarget.value;
                  })
                );
              }}
            />
            <AttributionTagSelector
              size="medium"
              label="Tag this bucket"
              selectedTags={bucket.tags || []}
              onChange={onAttributionTagChange}
            />
          </Stack>
        </SidebarContentWrapper>
      </ConditionGroupsFormWrapper>
    </>
  );
}

export default TrafficBucketForm;
